import { useEffect, useMemo, useRef } from 'react';

import { clearOverlay, setOverlay } from '../../engine/host.js';
import { getView, type EngineView } from '../../engine/prism.js';
import { serializeScene, type SceneDoc } from '../../lib/scene-doc.js';
import { eventSocket } from '../../lib/ws.js';
import { useScene } from '../../state/scene.js';
import { useWorkspace } from '../../state/workspace.js';
import { Viewport } from '../Viewport.js';
import { buildPreviewScene, PREVIEW_SCENE_PATH, previewSync } from './particle-model.js';
import { useParticlePreview } from './preview-store.js';

/**
 * The Particle Editor's live preview: an engine view in `preview` mode
 * (never the Scene view) showing only the edited system — with its
 * sub-emitters and collision planes — on a neutral ground under a
 * procedural sky. The scene is generated (`buildPreviewScene`) and served
 * through the file overlay; particle edits are sent as commands so the
 * running preview keeps its particles, structural changes reload it.
 */
export function ParticlePreviewView({ target }: { target: string }) {
  const doc = useScene((s) => s.doc);
  const viewRef = useRef<EngineView | null>(null);
  const shown = useRef<{ scene: SceneDoc; target: string } | null>(null);
  const scene = useMemo(() => (doc ? buildPreviewScene(doc, target) : null), [doc, target]);
  const sceneRef = useRef(scene);
  sceneRef.current = scene;
  const targetRef = useRef(target);
  targetRef.current = target;

  const load = async (view: EngineView, next: SceneDoc, id: string, frame: boolean) => {
    setOverlay(PREVIEW_SCENE_PATH, serializeScene(next));
    shown.current = { scene: next, target: id };
    await view.openScene(PREVIEW_SCENE_PATH);
    if (!view.alive) return;
    view.setOption('grid', 'true');
    useParticlePreview.getState().sync('preview');
    if (frame) view.setOption('frame', id);
  };

  // A changed document: commands when only components changed, else reload.
  useEffect(() => {
    const view = viewRef.current;
    if (!view?.alive || !scene) return;
    const last = shown.current;
    if (!last || last.target !== target) {
      void load(view, scene, target, true);
      return;
    }
    const plan = previewSync(last.scene, scene);
    if (!plan) return;
    if (plan.reload) {
      void load(view, scene, target, false);
      return;
    }
    shown.current = { scene, target };
    setOverlay(PREVIEW_SCENE_PATH, serializeScene(scene));
    // Sent in order; one failed command reloads the whole preview.
    void Promise.all(plan.commands.map((command) => view.command(command))).then((outcomes) => {
      if (outcomes.some((o) => !o.ok)) void load(view, scene, target, false);
    });
  }, [scene, target]);

  // Project files changed on disk (textures, materials, meshes): hot-reload them here too.
  useEffect(
    () =>
      eventSocket.subscribe((event) => {
        if (event.type !== 'file.changed' || event.projectId !== useWorkspace.getState().projectId) return;
        void getView('preview')?.fileChanged(event.path);
      }),
    [],
  );

  useEffect(() => () => clearOverlay(PREVIEW_SCENE_PATH), []);

  return (
    <Viewport
      mode="preview"
      className="pe-viewport"
      onReady={async (view) => {
        viewRef.current = view;
        const next = sceneRef.current;
        if (next) await load(view, next, targetRef.current, true);
      }}
      fallback={<div className="pe-viewport-empty muted">The live preview needs the engine runtime (pnpm engine:build).</div>}
    >
      <span className="pe-viewport-hint muted">Preview · orbit and zoom like the Scene view</span>
    </Viewport>
  );
}
